import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  X,
  ExternalLink,
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
  Database,
  Users,
  HardDrive,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Mail,
  Calendar,
  Clock,
  ArrowRight,
  Activity,
  Copy,
  Check,
  PlayCircle,
  Key,
  Shield,
  Layers,
  ChevronRight,
  Folder,
} from "lucide-react";
import { api } from "../../../lib/api";
import { toast } from "sonner";

export default function CompanyDetailDrawer({
  company,
  open,
  onClose,
  supportRequest,
  onEnterSupport,
  onRequestSupport,
  onToggleStatus,
  onRefresh,
}) {
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingIntegrity, setCheckingIntegrity] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    if (open && company?.id) {
      loadCompanyDetails(company.id);
    } else {
      setDetails(null);
    }
  }, [open, company?.id]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const loadCompanyDetails = async (accId) => {
    setLoading(true);
    try {
      const res = await api.get(`/super-admin/companies/${accId}`);
      setDetails(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load company details");
    } finally {
      setLoading(false);
    }
  };

  const handleRunIntegrityCheck = async () => {
    if (!company?.id) return;
    setCheckingIntegrity(true);
    try {
      const res = await api.post(`/super-admin/companies/${company.id}/integrity-check`);
      if (res.data?.ok) {
        toast.success(`SQLite Integrity Check Passed: ${res.data.result}`);
        if (details) {
          setDetails({
            ...details,
            storage: {
              ...details.storage,
              db_integrity: res.data.result,
              is_db_ok: true,
              db_size_formatted: res.data.size_formatted,
            },
          });
        }
      } else {
        toast.error(`Integrity Check Warning: ${res.data.error || res.data.result}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Integrity check failed to execute");
    } finally {
      setCheckingIntegrity(false);
    }
  };

  const copyToClipboard = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.success(`Copied ${fieldName} to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!open && !company) return null;

  const currentAcc = details?.account || company;
  const owner = details?.owner;
  const roles = details?.roles_count || company?.roles_count || { owner: 0, editor: 0, viewer: 0, total: 0 };
  const storage = details?.storage;
  const portfolio = details?.portfolio;
  const isSuspended = currentAcc?.status === "suspended";

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${
        open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Drawer Panel */}
      <div
        className={`fixed inset-y-0 right-0 max-w-xl w-full bg-[#0B0F17] border-l border-slate-800 shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-800/80 bg-slate-900/60 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-teal-400" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white truncate">
                  {currentAcc?.name || "Company Details"}
                </h3>
                {currentAcc?.id === "default" && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold tracking-wider bg-teal-500/10 text-teal-400 border border-teal-500/30 shrink-0">
                    PRIMARY
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[11px] text-slate-500 font-mono">
                  ID: {currentAcc?.id}
                </span>
                <span className="text-slate-600">•</span>
                {isSuspended ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-400 uppercase tracking-wider">
                    <ShieldAlert className="w-3 h-3" /> Suspended
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
                    <ShieldCheck className="w-3 h-3" /> Active
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {currentAcc?.id && (
              <button
                onClick={() => {
                  onClose();
                  navigate(`/super-admin/companies/${currentAcc.id}`);
                }}
                className="px-2.5 py-1.5 rounded-lg bg-teal-500/10 border border-teal-500/30 text-teal-400 hover:bg-teal-500/20 text-xs font-semibold flex items-center gap-1.5 transition-colors"
                title="Open Dedicated Full Page"
              >
                <span>Full Page</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
              title="Close (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-xs text-slate-300">
          {loading && !details ? (
            <div className="py-24 flex flex-col items-center justify-center gap-3 text-slate-400">
              <RefreshCw className="w-6 h-6 text-teal-400 animate-spin" />
              <span>Fetching telemetry and storage diagnostics...</span>
            </div>
          ) : (
            <>
              {/* Primary Contact & Metadata Card */}
              <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
                <div className="flex items-center justify-between text-[11px] font-bold tracking-wider uppercase text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-teal-400" />
                    Primary Owner & Contact
                  </span>
                  <span className="text-[10px] text-slate-500 font-mono">
                    Created {currentAcc?.created_at ? new Date(currentAcc.created_at).toLocaleDateString() : "—"}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800/80">
                    <div className="text-[10px] text-slate-500 uppercase font-semibold">Account Owner</div>
                    <div className="font-semibold text-white text-sm mt-0.5 truncate flex items-center gap-1.5">
                      <span>{owner?.username || company?.owner_username || "Not Registered"}</span>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800/80">
                    <div className="text-[10px] text-slate-500 uppercase font-semibold flex items-center justify-between">
                      <span>Primary Email</span>
                      {(owner?.email || company?.owner_email) && (
                        <button
                          onClick={() => copyToClipboard(owner?.email || company?.owner_email, "Email")}
                          className="text-slate-400 hover:text-white"
                          title="Copy Email"
                        >
                          {copiedField === "Email" ? <Check className="w-3 h-3 text-teal-400" /> : <Copy className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                    <div className="font-semibold text-teal-400 text-sm mt-0.5 truncate">
                      {owner?.email || company?.owner_email ? (
                        <a
                          href={`mailto:${owner?.email || company?.owner_email}`}
                          className="hover:underline"
                        >
                          {owner?.email || company?.owner_email}
                        </a>
                      ) : (
                        <span className="text-slate-500 font-normal">None recorded</span>
                      )}
                    </div>
                  </div>
                </div>

                {currentAcc?.subdomain && (
                  <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/40 border border-slate-800/60 text-[11px]">
                    <span className="text-slate-400">Custom Subdomain:</span>
                    <a
                      href={`https://${currentAcc.subdomain}.rentaltracker.io`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-400 hover:underline flex items-center gap-1 font-mono font-semibold"
                    >
                      <span>@{currentAcc.subdomain}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>

              {/* Members & Roles Breakdown Card */}
              <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
                <div className="flex items-center justify-between text-[11px] font-bold tracking-wider uppercase text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-blue-400" />
                    Team Roster & Roles Breakdown
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    {roles.total || roles.owner + roles.editor + roles.viewer || 0} Total
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2 pt-1">
                  <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800/80 text-center">
                    <div className="text-[10px] text-amber-400 font-bold uppercase tracking-wider flex items-center justify-center gap-1">
                      <Shield className="w-3 h-3 text-amber-400" /> Owners
                    </div>
                    <div className="text-lg font-extrabold text-white mt-1">
                      {roles.owner || 0}
                    </div>
                  </div>

                  <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800/80 text-center">
                    <div className="text-[10px] text-blue-400 font-bold uppercase tracking-wider flex items-center justify-center gap-1">
                      <Layers className="w-3 h-3 text-blue-400" /> Editors
                    </div>
                    <div className="text-lg font-extrabold text-white mt-1">
                      {roles.editor || 0}
                    </div>
                  </div>

                  <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800/80 text-center">
                    <div className="text-[10px] text-purple-400 font-bold uppercase tracking-wider flex items-center justify-center gap-1">
                      <Users className="w-3 h-3 text-purple-400" /> Viewers
                    </div>
                    <div className="text-lg font-extrabold text-white mt-1">
                      {roles.viewer || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Storage & DB Telemetry Card */}
              <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-3.5">
                <div className="flex items-center justify-between text-[11px] font-bold tracking-wider uppercase text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <HardDrive className="w-3.5 h-3.5 text-amber-400" />
                    Storage & Database Health
                  </span>
                  <button
                    onClick={handleRunIntegrityCheck}
                    disabled={checkingIntegrity}
                    className="text-[10px] font-semibold text-amber-400 hover:text-amber-300 flex items-center gap-1 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3 h-3 ${checkingIntegrity ? "animate-spin" : ""}`} />
                    <span>Run PRAGMA Check</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Database Info */}
                  <div className="p-3.5 rounded-lg bg-slate-950/60 border border-slate-800/80 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-500 uppercase font-semibold flex items-center gap-1">
                        <Database className="w-3 h-3 text-amber-400" />
                        SQLite Database
                      </span>
                      <span className="text-sm font-bold text-white">
                        {storage?.db_size_formatted || company?.db_size_formatted || "0 KB"}
                      </span>
                    </div>

                    <div className="pt-1 border-t border-slate-800/60 flex items-center justify-between text-[11px]">
                      <span className="text-slate-400">Integrity:</span>
                      {storage?.is_db_ok ? (
                        <span className="inline-flex items-center gap-1 font-bold text-emerald-400">
                          <CheckCircle2 className="w-3 h-3" /> OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 font-bold text-amber-400">
                          <AlertTriangle className="w-3 h-3" /> {storage?.db_integrity || "Unverified"}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Uploads Info */}
                  <div className="p-3.5 rounded-lg bg-slate-950/60 border border-slate-800/80 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-500 uppercase font-semibold flex items-center gap-1">
                        <FileText className="w-3 h-3 text-blue-400" />
                        Uploads & Files
                      </span>
                      <span className="text-sm font-bold text-white">
                        {storage?.storage_size_formatted || company?.storage_size_formatted || "0 KB"}
                      </span>
                    </div>

                    <div className="pt-1 border-t border-slate-800/60 flex items-center justify-between text-[11px]">
                      <span className="text-slate-400">Files Count:</span>
                      <span className="font-semibold text-slate-200">
                        {storage?.uploads_count ?? company?.uploads_count ?? 0} files
                      </span>
                    </div>
                  </div>
                </div>

                {/* Uploads Breakdown if available */}
                {storage?.uploads_categories && (
                  <div className="p-2.5 rounded-lg bg-slate-950/40 border border-slate-800/60 flex items-center justify-around text-[10px] font-mono text-slate-400">
                    <span>🖼️ Images: <strong className="text-slate-200">{storage.uploads_categories.images || 0}</strong></span>
                    <span>📄 Docs: <strong className="text-slate-200">{storage.uploads_categories.documents || 0}</strong></span>
                    <span>📦 Other: <strong className="text-slate-200">{storage.uploads_categories.other || 0}</strong></span>
                  </div>
                )}

                {/* Host Paths */}
                {currentAcc?.db_path && (
                  <div className="space-y-1.5 pt-1 text-[10px] font-mono">
                    <div className="flex items-center justify-between text-slate-500">
                      <span className="flex items-center gap-1">
                        <Folder className="w-3 h-3" /> DB Path:
                      </span>
                      <button
                        onClick={() => copyToClipboard(currentAcc.db_path, "DB Path")}
                        className="text-slate-400 hover:text-white"
                        title="Copy DB Path"
                      >
                        {copiedField === "DB Path" ? <Check className="w-2.5 h-2.5 text-teal-400" /> : <Copy className="w-2.5 h-2.5" />}
                      </button>
                    </div>
                    <div className="bg-slate-950/80 p-1.5 rounded border border-slate-800/60 text-slate-300 truncate">
                      {currentAcc.db_path}
                    </div>
                  </div>
                )}
              </div>

              {/* Portfolio & Activity KPIs */}
              <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
                <div className="flex items-center justify-between text-[11px] font-bold tracking-wider uppercase text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <Activity className="w-3.5 h-3.5 text-emerald-400" />
                    Portfolio Entities & Activity
                  </span>
                  {details?.portfolio?.latest_transaction_date && (
                    <span className="text-[10px] text-slate-500 font-mono">
                      Last Tx: {details.portfolio.latest_transaction_date}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 pt-1">
                  <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800/80 text-center">
                    <div className="text-[10px] text-slate-500 font-semibold uppercase">Properties</div>
                    <div className="text-base font-bold text-white mt-0.5">
                      {portfolio?.properties_count ?? company?.properties_count ?? 0}
                    </div>
                  </div>

                  <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800/80 text-center">
                    <div className="text-[10px] text-slate-500 font-semibold uppercase">Tenants</div>
                    <div className="text-base font-bold text-white mt-0.5">
                      {portfolio?.tenants_count ?? company?.tenants_count ?? 0}
                    </div>
                  </div>

                  <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800/80 text-center">
                    <div className="text-[10px] text-slate-500 font-semibold uppercase">Transactions</div>
                    <div className="text-base font-bold text-white mt-0.5">
                      {portfolio?.transactions_count ?? company?.transactions_count ?? 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Support Session & Administrative Controls */}
              <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
                <div className="text-[11px] font-bold tracking-wider uppercase text-slate-400 flex items-center gap-1.5">
                  <Key className="w-3.5 h-3.5 text-teal-400" />
                  Administrative Controls & Support
                </div>

                <div className="space-y-2 pt-1">
                  {/* Support Request Action */}
                  {supportRequest?.status === "approved" ? (
                    <button
                      onClick={() => onEnterSupport && onEnterSupport(supportRequest)}
                      className="w-full py-2.5 px-4 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20 transition-colors"
                    >
                      <PlayCircle className="w-4 h-4" />
                      <span>Enter Active Support Session ({supportRequest.requested_role.toUpperCase()})</span>
                    </button>
                  ) : supportRequest?.status === "pending" ? (
                    <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-center justify-between">
                      <span className="flex items-center gap-1.5 font-medium">
                        <Clock className="w-3.5 h-3.5" /> Awaiting Owner Approval
                      </span>
                      <span className="text-[10px] text-amber-400 font-mono">
                        {supportRequest.duration_hours}h requested
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={() => onRequestSupport && onRequestSupport(currentAcc)}
                      className="w-full py-2.5 px-4 rounded-xl bg-slate-900 border border-slate-700 hover:border-teal-500/50 hover:bg-slate-800 text-slate-200 font-semibold text-xs flex items-center justify-center gap-2 transition-colors"
                    >
                      <Key className="w-4 h-4 text-teal-400" />
                      <span>Request Delegated Support Access</span>
                    </button>
                  )}

                  {/* Suspend or Reactivate */}
                  <button
                    onClick={() => onToggleStatus && onToggleStatus(currentAcc, isSuspended ? "active" : "suspended")}
                    className={`w-full py-2 px-4 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-colors ${
                      isSuspended
                        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                        : "bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20"
                    }`}
                  >
                    {isSuspended ? (
                      <>
                        <ShieldCheck className="w-3.5 h-3.5" />
                        <span>Reactivate Company Account</span>
                      </>
                    ) : (
                      <>
                        <ShieldAlert className="w-3.5 h-3.5" />
                        <span>Suspend Company Account</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800/80 bg-slate-900/60 shrink-0 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 text-xs font-semibold transition-colors"
          >
            Close Preview
          </button>

          {currentAcc?.id && (
            <button
              onClick={() => {
                onClose();
                navigate(`/super-admin/companies/${currentAcc.id}`);
              }}
              className="px-4 py-2 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 text-xs font-bold flex items-center gap-1.5 transition-colors shadow-md shadow-teal-500/20"
            >
              <span>View Full Company Page</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
